import { useSelector, useDispatch } from "react-redux";
import { ModalCommonProps } from "./types";
import {
  ButtonGroup,
  StyledModal,
  WorkplaceContractFormModalTitle,
  WorkplaceContractFormModalHelpText,
} from "./styles";
import { Button } from "src/designsystem/Button/Button";
import { SessionType } from "src/modules/interface";
import { Link } from "../../../designsystem/Link/Link";
import { useMutation } from "@tanstack/react-query";
import { verifyFacilityUser as verifyFacilityUserApi } from "../api";
import { notification } from "antd";
import { EMAILS } from "src/constants/communications";
import { CbhFeatureFlag, useCbhFlag } from "@src/appV2/FeatureFlags";
import { logout } from "@src/modules/session";

function WorkplaceSignContractUnverifiedUser({ closeModal, onSubmit }: ModalCommonProps) {
  const { user, profile } = useSelector((state: SessionType) => state.session);
  const dispatch = useDispatch();

  const { showResendEmailCTA } = useCbhFlag(CbhFeatureFlag.WORKPLACE_SELF_SIGNUP, {
    defaultValue: {},
  });

  const isCognitoMagicLinkEnabled = useCbhFlag(
    CbhFeatureFlag.ENABLE_WORKPLACE_USER_COGNITO_MAGIC_LINKS,
    { defaultValue: false }
  );

  const verifyFacilityUser = useMutation({
    mutationFn: verifyFacilityUserApi,
  });

  const handleLogoutAndSignIn = async () => {
    closeModal();
    try {
      onSubmit();
    } catch {
      // Non-blocking: analytics failure should not prevent logout
    }
    try {
      await dispatch(logout());
    } catch {
      notification.error({
        message: "Failed to log out. Please try again.",
      });
    }
  };

  const onSubmitForVerificationEmail = async () => {
    if (!user?._id || !profile?.userId) {
      notification.error({
        message: "Unexpected error occurred, when sending the email!",
      });
      return;
    }
    try {
      const { success } = await verifyFacilityUser.mutateAsync({
        facilityUserId: user._id,
        facilityId: profile.userId,
      });
      if (!success) {
        throw new Error(
          "Unfortunately! Message sending failed. Please try again later or contact us!"
        );
      }
      notification.success({
        message: "Resending the verification email is successful!",
      });
      onSubmit();
      closeModal();
      // FIXME: Automatically disabled during ESLint config update
    } catch {
      notification.error({
        message: "Error in sending the verification email. Please try again after some time.",
      });
    }
  };

  if (isCognitoMagicLinkEnabled) {
    return (
      <StyledModal visible closable onCancel={closeModal} footer={null} cancelText={null}>
        <WorkplaceContractFormModalTitle level={3}>
          As a last step, we need to verify your email
        </WorkplaceContractFormModalTitle>
        <WorkplaceContractFormModalHelpText>
          To verify your account, please log out and sign back in using the email option. When you
          sign in again, your email will be automatically verified and you&apos;ll have full access
          to start posting shifts!
        </WorkplaceContractFormModalHelpText>
        <br />
        <WorkplaceContractFormModalHelpText>
          If you need help, please contact us at{" "}
          <Link href={`mailto:${EMAILS.getAccess}`}>{EMAILS.getAccess}</Link>.
        </WorkplaceContractFormModalHelpText>
        <ButtonGroup>
          <Button variant="primary" onClick={handleLogoutAndSignIn}>
            Log out and sign in
          </Button>
          <Button variant="secondary" onClick={closeModal}>
            Later
          </Button>
        </ButtonGroup>
      </StyledModal>
    );
  }

  return (
    <StyledModal visible closable onCancel={closeModal} footer={null} cancelText={null}>
      <WorkplaceContractFormModalTitle level={3}>
        As a last step, we need to verify your email
      </WorkplaceContractFormModalTitle>
      <WorkplaceContractFormModalHelpText>
        Check your inbox at <strong>{user?.email}</strong> to verify your account. Once you click
        the link in the email, you&apos;ll have full access and can start posting shifts!
      </WorkplaceContractFormModalHelpText>
      <br />
      <WorkplaceContractFormModalHelpText>
        Didn&apos;t get the email? Be sure to check your spam folder. If you still need help, please
        contact us at <Link href={`mailto:${EMAILS.getAccess}`}>{EMAILS.getAccess}</Link>.
      </WorkplaceContractFormModalHelpText>
      <ButtonGroup>
        {showResendEmailCTA && (
          <Button
            variant="secondary"
            onClick={onSubmitForVerificationEmail}
            isLoading={verifyFacilityUser.isLoading}
          >
            Resend email
          </Button>
        )}
        <Button variant="primary" onClick={closeModal}>
          OK
        </Button>
      </ButtonGroup>
    </StyledModal>
  );
}

export { WorkplaceSignContractUnverifiedUser };
